/* eslint-disable react/destructuring-assignment */
import { Card } from 'Card';
import { type ComponentProps, type ReactNode, memo } from 'react';
import { twMerge } from 'tailwind-merge';
import { Text } from 'Text';
import { useTutorBotBottomSpacing } from 'TutorBotChat';
import { parseHtml } from 'FrontRoyalAngular/html.utils';
import { IntroMessages } from './IntroMessages/IntroMessages';

type Props = {
    Button: ReactNode;
    Bottom?: ReactNode;
    cardHeader?: string;
    cardTexts?: string[];
    cardWidth?: ComponentProps<typeof Card>['width'];
} & ({ type: 'intro'; introMessages: string[] } | { type: 'custom-main'; Main: ReactNode });

function MessagesContainer({ children }: { children: ReactNode }) {
    return (
        <div className="relative mb-3 flex flex-1 flex-col overflow-y-auto pb-1 pt-[105px] scrollbar-hide sm:mb-0 sm:pt-11">
            {children}
        </div>
    );
}

function BottomAlignedWithButtonComponent(props: Props) {
    const spacingBeneathLowestButton = useTutorBotBottomSpacing();

    return (
        <div className="relative flex h-full w-full flex-col justify-end">
            {props.cardHeader && props.cardTexts && (
                <div className="absolute top-20 z-10 flex w-full justify-center">
                    <Card width={props.cardWidth ?? 342} header={<Text as="h2">{props.cardHeader}</Text>}>
                        <Text>
                            {props.cardTexts.map(text => (
                                <p key={text}>{parseHtml(text)}</p>
                            ))}
                        </Text>
                    </Card>
                </div>
            )}

            {props.type === 'intro' && (
                <MessagesContainer>
                    <div className="absolute bottom-0 mt-auto">
                        <IntroMessages messages={props.introMessages} />
                    </div>
                </MessagesContainer>
            )}

            {props.type === 'custom-main' && <MessagesContainer>{props.Main}</MessagesContainer>}

            <div>
                <div className="flex w-full justify-center">{props.Button}</div>

                <div className={twMerge('flex w-full items-center justify-center', spacingBeneathLowestButton)}>
                    {props.Bottom || <span>&nbsp;</span>}
                </div>
            </div>
        </div>
    );
}

export const BottomAlignedWithButton = memo(
    BottomAlignedWithButtonComponent,
) as typeof BottomAlignedWithButtonComponent;
